// Polling-based hook for the intercom call queue. SSE push is planned
// in a follow-up; today the pages poll on a 5s interval which is fine
// for human-operator latency (a real call queue tolerates a few seconds
// of staleness, and the operator's first response is to refresh anyway).

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient, ApiError } from './api.js';
import type { IntercomCall } from '@ng-unifi/domain';

interface ListResponse<T> {
  data: T[];
}

interface ItemResponse<T> {
  data: T;
}

const POLL_INTERVAL_MS = 5000;

export interface UseIntercomCallsResult {
  calls: IntercomCall[] | null;
  error: string | null;
  loading: boolean;
  refresh: () => Promise<void>;
  answer: (id: string, notes?: string) => Promise<IntercomCall>;
  end: (id: string, opts?: { reason?: string; notes?: string }) => Promise<IntercomCall>;
}

export function useIntercomCalls(): UseIntercomCallsResult {
  const [calls, setCalls] = useState<IntercomCall[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const cancelledRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const res = await apiClient.get<ListResponse<IntercomCall>>('/api/v1/callbox/calls');
      if (!cancelledRef.current) {
        setCalls(res.data);
        setError(null);
      }
    } catch (e) {
      if (!cancelledRef.current) {
        setError(e instanceof ApiError ? e.message : 'Failed to load calls');
      }
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    void refresh();
    const id = setInterval(() => {
      void refresh();
    }, POLL_INTERVAL_MS);
    return () => {
      cancelledRef.current = true;
      clearInterval(id);
    };
  }, [refresh]);

  const answer = useCallback(
    async (id: string, notes?: string) => {
      const res = await apiClient.post<ItemResponse<IntercomCall>>(
        `/api/v1/callbox/calls/${id}/answer`,
        notes !== undefined ? { notes } : {},
      );
      // Optimistic local update so the UI feels responsive even before
      // the next poll tick.
      setCalls((prev) =>
        prev ? prev.map((c) => (c.id === id ? res.data : c)) : prev,
      );
      return res.data;
    },
    [],
  );

  const end = useCallback(
    async (id: string, opts?: { reason?: string; notes?: string }) => {
      const res = await apiClient.post<ItemResponse<IntercomCall>>(
        `/api/v1/callbox/calls/${id}/end`,
        opts ?? {},
      );
      setCalls((prev) =>
        prev ? prev.map((c) => (c.id === id ? res.data : c)) : prev,
      );
      return res.data;
    },
    [],
  );

  return { calls, error, loading, refresh, answer, end };
}

export function useIntercomCall(id: string | undefined): {
  call: IntercomCall | null;
  error: string | null;
  loading: boolean;
  refresh: () => Promise<void>;
} {
  const [call, setCall] = useState<IntercomCall | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const cancelledRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!id) return;
    try {
      const res = await apiClient.get<ItemResponse<IntercomCall>>(
        `/api/v1/callbox/calls/${id}`,
      );
      if (!cancelledRef.current) {
        setCall(res.data);
        setError(null);
      }
    } catch (e) {
      if (!cancelledRef.current) {
        setError(e instanceof ApiError ? e.message : 'Failed to load call');
      }
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    cancelledRef.current = false;
    void refresh();
    const tid = setInterval(() => {
      void refresh();
    }, POLL_INTERVAL_MS);
    return () => {
      cancelledRef.current = true;
      clearInterval(tid);
    };
  }, [id, refresh]);

  return { call, error, loading, refresh };
}

export function statusLabel(status: IntercomCall['status']): string {
  switch (status) {
    case 'queued':
      return 'Ringing';
    case 'answered':
      return 'In call';
    case 'ended':
      return 'Ended';
    case 'missed':
      return 'Missed';
  }
}

export function statusTone(status: IntercomCall['status']): 'warning' | 'success' | 'neutral' | 'danger' {
  if (status === 'queued') return 'warning';
  if (status === 'answered') return 'success';
  if (status === 'missed') return 'danger';
  return 'neutral';
}
